import { type FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { notifications } from '@mantine/notifications';
import { useShiftAuth } from '../../contexts/ShiftAuthProvider';
import { ShiftAuthRequestError } from '../../services/auth/shift-auth.service';
import { shiftEmployeesService } from '../../services/employees/shift-employees.service';
import { resolveShiftCapabilities } from '../../utils/auth/shift-permissions';
import type {
  ShiftEmployeeDirectoryManagerRow,
  ShiftEmployeeCreateInput,
  ShiftEmployeeDirectoryWorkerRow,
} from '../../models/employees.types';
import { type SortField, type SortDirection } from './employees-page.types';
import {
  type EmployeeFormState,
  type ShiftSectionOption,
} from '../../components/EmployeeFormFields/employee-form.types';
import {
  createEmptyEmployeeForm,
  normalizeSearchText,
  compareByField,
  isManagerEmployeeRow,
  formatSexLabel,
} from './employees-page.utils';
import {
  toSectionOptions,
  toFormState,
  buildEmployeeUpdatePayload,
} from '../../components/EmployeeFormFields/employee-form.utils';
import { resolveRequestErrorMessage } from '../../utils/request-errors.utils';
import { getTodayIsoDate } from '../../utils/date.utils';

export function useEmployeesPageController() {
  const { user } = useShiftAuth();

  const { isManager, canEditEmployees, canCreateEmployees } = resolveShiftCapabilities(user);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [employees, setEmployees] = useState<
    Array<ShiftEmployeeDirectoryManagerRow | ShiftEmployeeDirectoryWorkerRow>
  >([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<SortField>('firstName');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [isExporting, setIsExporting] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isCreateSaving, setIsCreateSaving] = useState(false);
  const [createForm, setCreateForm] = useState<EmployeeFormState>(() => createEmptyEmployeeForm());
  const [createUsername, setCreateUsername] = useState('');
  const [createPassword, setCreatePassword] = useState('');
  const [editingEmployeePk, setEditingEmployeePk] = useState<number | null>(null);
  const [isEditLoading, setIsEditLoading] = useState(false);
  const [isEditSaving, setIsEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<EmployeeFormState | null>(null);
  const [editSectionLabel, setEditSectionLabel] = useState<string | null>(null);
  const [sectionOptions, setSectionOptions] = useState<ShiftSectionOption[]>([]);

  const employeeWriteInFlightRef = useRef(false);
  const editBaselineRef = useRef<EmployeeFormState | null>(null);

  const filteredAndSortedEmployees = useMemo(() => {
    const normalized = normalizeSearchText(searchTerm);
    const filtered =
      normalized.length === 0
        ? employees
        : employees.filter((employee) => {
            const fullName = `${employee.firstName} ${employee.lastName}`;
            return normalizeSearchText(fullName).includes(normalized);
          });

    return [...filtered].sort((left, right) => compareByField(left, right, sortField, sortDirection));
  }, [employees, searchTerm, sortField, sortDirection]);

  const loadEmployees = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const rows = await shiftEmployeesService.listDirectory();
      setEmployees(rows);

      if (isManager) {
        const sections = await shiftEmployeesService.listSections();
        setSectionOptions(toSectionOptions(sections));
      } else {
        setSectionOptions([]);
      }
    } catch (requestError: unknown) {
      setError(resolveRequestErrorMessage(requestError, 'לא ניתן לטעון את רשימת העובדים.'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void loadEmployees();
  }, []);

  const onToggleSort = (field: SortField) => {
    setSortDirection((current) => {
      if (sortField !== field) {
        setSortField(field);
        return 'asc';
      }

      return current === 'asc' ? 'desc' : 'asc';
    });
  };

  const onOpenEditModal = async (employeePk: number) => {
    editBaselineRef.current = null;
    setEditingEmployeePk(employeePk);
    setEditError(null);
    setEditForm(null);
    setEditSectionLabel(null);
    setIsEditLoading(true);

    try {
      const row = await shiftEmployeesService.getEmployeeDetails(employeePk);
      if (!isManagerEmployeeRow(row)) {
        throw new ShiftAuthRequestError('אין הרשאה לעריכת עובד זה.', 403, false);
      }

      const baseline = toFormState(row);
      editBaselineRef.current = baseline;
      setEditForm(baseline);
      setEditSectionLabel(row.sectionName);
    } catch (requestError: unknown) {
      setEditError(resolveRequestErrorMessage(requestError, 'לא ניתן לטעון את פרטי העובד לעריכה.'));
    } finally {
      setIsEditLoading(false);
    }
  };

  const onCloseEditModal = () => {
    editBaselineRef.current = null;
    setEditingEmployeePk(null);
    setEditForm(null);
    setIsEditLoading(false);
    setIsEditSaving(false);
    setEditError(null);
    setEditSectionLabel(null);
  };

  const onSaveEmployeeEdit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!editForm || !editingEmployeePk || !editBaselineRef.current || employeeWriteInFlightRef.current) {
      return;
    }

    const payload = buildEmployeeUpdatePayload(editForm, true, editBaselineRef.current);
    if (Object.keys(payload).length === 0) {
      onCloseEditModal();
      return;
    }

    employeeWriteInFlightRef.current = true;
    setEditError(null);
    setIsEditSaving(true);

    try {
      const updated = await shiftEmployeesService.updateEmployee(editingEmployeePk, payload);
      setEmployees((current) =>
        current.map((row) =>
          isManagerEmployeeRow(row) && row.employeePk === updated.employeePk ? updated : row,
        ),
      );
      notifications.show({ color: 'green', message: 'פרטי העובד נשמרו בהצלחה.' });
      onCloseEditModal();
    } catch (requestError: unknown) {
      notifications.show({
        color: 'red',
        title: 'שגיאה',
        message: resolveRequestErrorMessage(requestError, 'שמירת העובד נכשלה.'),
      });
    } finally {
      employeeWriteInFlightRef.current = false;
      setIsEditSaving(false);
    }
  };

  const onExport = async () => {
    setIsExporting(true);

    try {
      const XLSX = await import('xlsx');

      const rows = filteredAndSortedEmployees.map((employee) => {
        if (!isManager || !isManagerEmployeeRow(employee)) {
          return {
            'שם פרטי': employee.firstName,
            'שם משפחה': employee.lastName,
            טלפון: employee.phoneNumber ?? '',
          };
        }

        return {
          'שם פרטי': employee.firstName,
          'שם משפחה': employee.lastName,
          אימייל: employee.email ?? '',
          טלפון: employee.phoneNumber ?? '',
          מין: formatSexLabel(employee.sex),
          'תאריך לידה': employee.birthdate ?? '',
          'תעודת זהות': employee.idNumber ?? '',
          מחלקה: employee.sectionName ?? '',
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Employees');
      XLSX.writeFile(workbook, `shift-employees-${getTodayIsoDate()}.xlsx`);
      notifications.show({ color: 'green', message: 'הקובץ יוצא בהצלחה.' });
    } catch {
      notifications.show({ color: 'red', title: 'שגיאה', message: 'ייצוא לאקסל נכשל. נסו שוב.' });
    } finally {
      setIsExporting(false);
    }
  };

  const onCloseCreateModal = () => {
    setIsCreateOpen(false);
    setCreateForm(createEmptyEmployeeForm());
    setCreateUsername('');
    setCreatePassword('');
  };

  const onCreateEmployee = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (employeeWriteInFlightRef.current) {
      return;
    }
    employeeWriteInFlightRef.current = true;
    setIsCreateSaving(true);
    try {
      const basePayload = buildEmployeeUpdatePayload(createForm, true);
      const payload: ShiftEmployeeCreateInput = {
        ...basePayload,
        firstName: createForm.firstName.trim(),
        lastName: createForm.lastName.trim(),
        username: createUsername.trim(),
        temporaryPassword: createPassword,
      };
      const created = await shiftEmployeesService.createEmployee(payload);
      setEmployees((current) => [...current, created]);
      notifications.show({ color: 'green', message: 'העובד וחשבון ה-SHIFT נוצרו בהצלחה.' });
      onCloseCreateModal();
    } catch (requestError: unknown) {
      notifications.show({
        color: 'red',
        title: 'שגיאה',
        message: resolveRequestErrorMessage(requestError, 'יצירת העובד נכשלה.'),
      });
    } finally {
      employeeWriteInFlightRef.current = false;
      setIsCreateSaving(false);
    }
  };

  return {
    isManager,
    canEditEmployees,
    canCreateEmployees,
    isLoading,
    error,
    employees,
    searchTerm,
    setSearchTerm,
    sortField,
    sortDirection,
    isExporting,
    isCreateOpen,
    setIsCreateOpen,
    isCreateSaving,
    createForm,
    setCreateForm,
    createUsername,
    setCreateUsername,
    createPassword,
    setCreatePassword,
    editingEmployeePk,
    isEditLoading,
    isEditSaving,
    editError,
    editForm,
    setEditForm,
    editSectionLabel,
    sectionOptions,
    filteredAndSortedEmployees,
    loadEmployees,
    onToggleSort,
    onOpenEditModal,
    onCloseEditModal,
    onSaveEmployeeEdit,
    onExport,
    onCloseCreateModal,
    onCreateEmployee,
  };
}
